import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';
import { useScope, brandColorVar } from '@/context/ScopeContext';
import { KPIStatCard } from '@/components/KPIStatCard';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import TESTIDS from '@/constants/testIds';

export default function Dashboard() {
    const { brandId, accountId, brands, accounts } = useScope();
    const [overview, setOverview] = useState(null);
    const [activity, setActivity] = useState([]);
    const [scheduled, setScheduled] = useState([]);

    useEffect(() => {
        const params = {};
        if (brandId) params.brand_id = brandId;
        if (accountId) params.account_id = accountId;
        apiClient.get('/dashboard/overview', { params }).then((r) => setOverview(r.data));
        apiClient
            .get('/dashboard/recent_activity', { params: brandId ? { brand_id: brandId } : {} })
            .then((r) => setActivity(r.data));
        apiClient
            .get('/content/scheduled', { params: brandId ? { brand_id: brandId } : {} })
            .then((r) => setScheduled(r.data));
    }, [brandId, accountId]);

    const counts = overview?.counts || {};

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight">Welcome back, operator</h1>
                <p className="text-sm text-muted-foreground">
                    Multi-brand, multi-account command center. Scope is{' '}
                    <span className="font-mono text-foreground">
                        {brandId ? brands.find((b) => b.id === brandId)?.name : 'all brands'}
                    </span>
                    {accountId ? (
                        <>
                            {' · '}
                            <span className="font-mono text-foreground">
                                @{accounts.find((a) => a.id === accountId)?.handle}
                            </span>
                        </>
                    ) : null}
                </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="dashboard-kpi-grid">
                <KPIStatCard label="Brands" value={counts.brands ?? '—'} accent="neutral" />
                <KPIStatCard label="Accounts" value={counts.accounts ?? '—'} accent="neutral" />
                <KPIStatCard label="Drafts" value={counts.drafts ?? '—'} accent="info" />
                <KPIStatCard label="Scheduled" value={counts.scheduled ?? '—'} accent="info" />
                <KPIStatCard label="Pending publish" value={counts.published_pending ?? '—'} accent="warning" />
                <KPIStatCard label="Failed" value={counts.published_failed ?? '—'} accent="danger" />
                <KPIStatCard label="Open comments" value={counts.pending_comments ?? '—'} accent="info" />
                <KPIStatCard label="Flagged" value={counts.flagged_conversations ?? '—'} accent="danger" />
                <KPIStatCard label="Pending DMs" value={counts.pending_dms ?? '—'} accent="warning" />
                <KPIStatCard label="Safety events" value={counts.safety_events_open ?? '—'} accent="warning" />
            </div>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="col-span-2 border-border bg-card p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                Upcoming scheduled
                            </div>
                            <h2 className="text-base font-semibold tracking-tight">Next sends</h2>
                        </div>
                        <Link to="/admin/social/calendar" className="inline-flex items-center text-xs text-[hsl(var(--link))] hover:text-[hsl(var(--link-hover))]">
                            Open calendar <ArrowRight className="ml-1 h-3 w-3" />
                        </Link>
                    </div>
                    {scheduled.length === 0 ? (
                        <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-4 text-sm text-muted-foreground">
                            Nothing scheduled in this scope yet. Open the composer to draft and schedule a post.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {scheduled.slice(0, 6).map((s) => {
                                const brand = brands.find((b) => b.id === s.brand_id);
                                return (
                                    <div
                                        key={s.id}
                                        className="flex items-center justify-between rounded-md border border-border bg-[hsl(var(--surface-2))] px-3 py-2"
                                        style={{ '--brand-accent-current': brandColorVar(brand?.color_slot) }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="inline-block h-2 w-2 rounded-full"
                                                style={{ backgroundColor: `hsl(${brandColorVar(brand?.color_slot)})` }}
                                            />
                                            <div>
                                                <div className="text-sm font-medium">{brand?.name || 'Unknown brand'}</div>
                                                <div className="font-mono text-[11px] text-muted-foreground">
                                                    {new Date(s.scheduled_at).toLocaleString()} • {(s.destination_account_ids || []).length} destinations
                                                </div>
                                            </div>
                                        </div>
                                        <StatusBadge status={s.status} />
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Card>

                <Card className="border-border bg-card p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <div>
                            <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                                Activity
                            </div>
                            <h2 className="text-base font-semibold tracking-tight">Recent operator actions</h2>
                        </div>
                        <Link to="/admin/social/audit" className="inline-flex items-center text-xs text-[hsl(var(--link))] hover:text-[hsl(var(--link-hover))]">
                            Audit log <ArrowRight className="ml-1 h-3 w-3" />
                        </Link>
                    </div>
                    {activity.length === 0 ? (
                        <div className="rounded-md border border-dashed border-border bg-[hsl(var(--surface-2))] p-4 text-sm text-muted-foreground">
                            No activity yet. Generate your first piece of content to seed history.
                        </div>
                    ) : (
                        <ul className="divide-y divide-border">
                            {activity.slice(0, 8).map((a) => (
                                <li key={a.id} className="flex items-start gap-2 py-2 text-xs">
                                    <div className="font-mono text-[11px] text-muted-foreground whitespace-nowrap w-[140px]">
                                        {new Date(a.timestamp).toLocaleString()}
                                    </div>
                                    <div className="flex-1">
                                        <div className="text-foreground">{a.action}</div>
                                        <div className="font-mono text-[11px] text-muted-foreground">
                                            {a.actor} • {a.target_type}
                                        </div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
}
